/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Wizard Step Progress Indicator Component (Task 10.2.3 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { Check, Settings, Sparkles, ListTree, Flame } from 'lucide-react';
import { useI18n } from '../../stores/useI18nStore';

interface StepIndicatorProps {
  currentStep: 1 | 2 | 3 | 4;
  completedSteps: number[];
  onSelectStep: (step: 1 | 2 | 3 | 4) => void;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  completedSteps,
  onSelectStep,
}) => {
  const { t } = useI18n();

  const steps = [
    {
      step: 1,
      title: t('wizard.step1'),
      subtitle: t('wizard.step1Desc'),
      icon: Settings,
    },
    {
      step: 2,
      title: t('wizard.step2'),
      subtitle: t('wizard.step2Desc'),
      icon: Sparkles,
    },
    {
      step: 3,
      title: t('wizard.step3'),
      subtitle: t('wizard.step3Desc'),
      icon: ListTree,
    },
    {
      step: 4,
      title: t('wizard.step4'),
      subtitle: t('wizard.step4Desc'),
      icon: Flame,
    },
  ];

  return (
    <div className="w-full bg-surface border border-surface-border rounded-2xl p-4 shadow-glass">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {steps.map((s) => {
          const isCurrent = currentStep === s.step;
          const isCompleted = completedSteps.includes(s.step);
          const canClick = isCompleted || s.step <= currentStep;
          const StepIcon = s.icon;

          return (
            <button
              key={s.step}
              type="button"
              disabled={!canClick}
              onClick={() => canClick && onSelectStep(s.step as 1 | 2 | 3 | 4)}
              className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-200 ${
                isCurrent
                  ? 'border-primary bg-primary/10 shadow-glow-primary/20 ring-1 ring-primary/40'
                  : isCompleted
                  ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 cursor-pointer'
                  : 'border-surface-border/60 bg-surface-elevated/40 opacity-50 cursor-not-allowed'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  isCurrent
                    ? 'bg-primary text-primary-fg shadow-glow-primary'
                    : isCompleted
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'bg-muted text-foreground-muted'
                }`}
              >
                {isCompleted && !isCurrent ? (
                  <Check className="w-4 h-4 stroke-[2.5]" />
                ) : (
                  <StepIcon className="w-4 h-4" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-foreground-muted">
                    {t('wizard.stepNumber', { step: `0${s.step}` })}
                  </span>
                  {isCurrent && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  )}
                </div>
                <div className="text-xs font-heading font-bold text-foreground truncate">
                  {s.title}
                </div>
                <div className="text-[11px] text-foreground-muted truncate hidden sm:block">
                  {s.subtitle}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
